/**
 * Video tools.
 */
H_Tools.video = (() => {
    /**
     * Add play and pause functions to the supplied video element.
     *
     * This is done to create a common interface for controlling playback for all types of videos.
     *
     * @param {*} video A video element
     */
    function allowPlayPause(video) {
        allowPlayPauseVideo(video);
        allowPlayPauseVimeo(video);
    }

    /**
     * Add play and pause functions to the supplied video element if it contains a Vimeo iframe.
     *
     * @param {*} video A video element
     */
    function allowPlayPauseVimeo(el) {
        const iframe = el
            .closest('.js-video')
            ?.querySelector('.js-vimeo-iframe');

        if (!iframe) {
            return;
        }

        let player =
            typeof Vimeo !== 'undefined' ? new Vimeo.Player(iframe) : null;

        el.pause = () => {
            // console.log('Vimeo video was paused');

            if (player) {
                player.pause();
            } else {
                /**
                 * Fallback for the "pause" function.
                 *
                 * The video is hidden instead of being paused when pause functionality is not
                 * available. This happens when Vimeo Player SDK is blocked by CookieBot for example.
                 */
                iframe.style.display = 'none';
            }
        };
        el.play = () => {
            // console.log('Vimeo video was started');

            if (player) {
                player.play();
            } else {
                /**
                 * Fallback for the "play" function.
                 *
                 * The video is shown instead of starting playback when pause functionality is not
                 * available. This happens when Vimeo Player SDK is blocked by CookieBot for example.
                 */
                iframe.style.display = 'block';
            }
        };
    }

    /**
     * Add play and pause functions to the supplied video element if it contains a regular `<video>`
     * tag.
     *
     * @param {*} video A video element
     */
    function allowPlayPauseVideo(el) {
        let video = el
            ?.closest('.js-video')
            ?.querySelector('.js-video-element');

        if (!video) {
            return;
        }

        el.pause = () => {
            // console.log('Regular video was paused');
            video.pause();
        };
        el.play = () => {
            // console.log('Regular video was started');
            video.play();
        };
    }

    /**
     * Pauses all videos inside the supplied element.
     *
     * @param {*} el An element to look inside for videos
     */
    function pauseAll(el) {
        el.querySelectorAll('.js-video').forEach((video) => {
            togglePauseState(video, true);
        });
    }

    /**
     * Checks if the user prefers reduced motion and pauses all videos inside the supplied element
     * if they do.
     *
     * @param {*} el An element to look inside for videos
     */
    function reduceMotion(el) {
        const mediaQuery = window.matchMedia(
            '(prefers-reduced-motion: no-preference)'
        );

        if (!mediaQuery || mediaQuery.matches) {
            return;
        }

        pauseAll(el);
    }

    /**
     * Refreshes the play/pause state of the supplied video element.
     *
     * @param {*} video A video element
     */
    function refresh(video) {
        if (!video?.play || !video?.pause) {
            // console.error("Video doesn't have play or pause functions!");
            return;
        }

        if (shouldBePaused(video)) {
            video.pause();
        } else {
            video.play();
        }
    }

    /**
     * Sets the button state of the supplied pause button.
     *
     * @param {*} button A pause button element (or a nearby element)
     * @param {*} paused If `true` the button state will be set to paused, if `false` the button
     *                   state will be set to play
     */
    function setPauseState(button, paused) {
        if (!button) {
            return;
        }

        // if we are given an element that isn't the button element, find the button element
        if (button.tagName !== 'BUTTON') {
            button = button
                ?.closest('.js-video')
                ?.querySelector('.js-pause-button');
        }

        if (!button) {
            return;
        }

        button.classList.toggle('-playing', paused);
        button.classList.toggle('-paused', !paused);

        let text = button.querySelector('.js-pause-button-text');

        if (!text) {
            return;
        }

        text.innerText = button.getAttribute(
            'data-text-' + (paused ? 'pause' : 'play')
        );
    }

    /**
     * Checks if the supplied video element should be paused. Returns a representative boolean
     * value.
     *
     * @param {*} video A video element
     * @returns         `true` if the supplied video element should be paused, `false` otherwise
     */
    function shouldBePaused(video) {
        if (document.hidden) {
            return true;
        }

        if (video.getAttribute('data-in-viewport') === 'false') {
            return true;
        }

        return video.getAttribute('data-user-paused') === 'true';
    }

    /**
     * Toggles the user's pause state of the supplied video.
     *
     * The user's pause state is the state the user wants the video to be in (playing or paused).
     * The user indicates their desire via the reduced motion preference on their device and by
     * using the pause buttons.
     *
     * Note that the user's pause state is separate from the actual pause state of the video, which
     * may change depending whether the video is visible in the viewport.
     *
     * @param {*} video      A video element
     * @param {*} forcePause Whether to force the video to pause regardless of its current state
     */
    function togglePauseState(video, forcePause = false) {
        if (!video) {
            return;
        }

        const paused = forcePause
            ? false
            : video.getAttribute('data-user-paused') === 'true';

        video.setAttribute('data-user-paused', paused ? 'false' : 'true');
        setPauseState(video.querySelector('.js-pause-button'), paused);
        refresh(video);
    }

    // Return an object to expose inner functions
    return {
        allowPlayPause: allowPlayPause,
        reduceMotion: reduceMotion,
        refresh: refresh,
        setPauseState: setPauseState,
        togglePauseState: togglePauseState,
    };
})();
